// app/frontend/pages/RecurringEvent/RecurringEventShow.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  Image,
  Text,
  Badge,
  Group,
  Button,
  Title,
  Stack,
  ActionIcon,
  Tooltip,
  Alert,
  Menu,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCalendarRepeat, IconShare, IconCopy, IconCalendarPlus, IconInfoCircle } from '@tabler/icons-react';
import { useUser } from '../../src/context/UserContext';
import EventCard from '../../components/Cards/EventCard';

import ManageBadge from '../../components/ManageBadge';

// Define types for our data
interface RecurringEvent {
  id: number;
  title: string;
  slug: string;
  start_date: string;
  start_time: string;
  end_time: string;
  recurrence_pattern: string;
  num_occurrences: number;
  re_timezone: string;
  spans_next_day: boolean;
  ordinal_occurrence?: string;
  occurrence_day?: string;
  image_url?: string;
  created_at: string;
  updated_at: string;
}

interface Event {
  id: number;
  name: string;
  slug: string;
  description: string;
  event_start_date: string;
  event_end_date: string;
  address: string;
  is_recurring: boolean;
  recurring_event_id: number;
  timezone: string;
  event_status_id: number;
  event_image_url?: string;
  artists: { id: number; name: string }[];
  videos: { id: number; url: string }[];
  event_types: { id: number; name: string }[];
  dance_styles: { id: number; name: string }[];
  city?: { name: string };
  state?: string;
  country?: {
    name: string;
    flag_emoji: string;
  };
}

interface User {
  id: number;
  username?: string;
  email: string;
}

interface RecurringEventShowProps {
  recurringEvent: RecurringEvent;
  events: Event[];
  users: User[];
  hasPendingEvents: boolean;
}

const RecurringEventShow: React.FC<RecurringEventShowProps> = ({ recurringEvent, events, users, hasPendingEvents }) => {
  const { user } = useUser();
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  // Check if current user has manage access
  const hasManageAccess =
    user && (users.some((u) => u.id === user.id) || (user.roles && user.roles.includes('superadmin')));

  // Check if user is superadmin
  const isSuperadmin = user && user.roles && user.roles.includes('SuperAdmin');

  // Handle approve all events
  const handleApproveAll = async () => {
    try {
      const response = await fetch(`/re/${recurringEvent.slug}/approve_all`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
      });

      if (response.ok) {
        notifications.show({
          title: 'Success',
          message: 'All pending events have been approved',
          color: 'green',
        });
        // Reload the page to show updated status
        window.location.reload();
      } else {
        throw new Error('Failed to approve events');
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to approve events. Please try again.',
        color: 'red',
      });
    }
  };

  // Copy link to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      notifications.show({
        title: 'Link copied',
        message: 'Recurring event link copied to clipboard!',
        color: 'orange',
      });
    });
  };

  // Handle event card click
  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    // You could open a drawer here or navigate to the event details page
    window.location.href = `/events/${event.slug}`;
  };

  useEffect(() => {
    console.log('Has Pending Events:', hasPendingEvents);
  }, []);

  return (
    <Container size="xl" py="xl">
      {/* Manage Badge */}
      {hasManageAccess && (
        <ManageBadge
          entityType="event"
          entitySlug={recurringEvent.slug}
          userHasAccess={true}
          customText="You have manage access for this recurring event."
          editUrl={`/recurring_events/${recurringEvent.slug}/edit`}
        />
      )}

      {/* Approve All Button for Superadmins */}
      {isSuperadmin && hasPendingEvents && (
        <Button color="green" size="xl" variant="light" mb="md" onClick={handleApproveAll}>
          Approve All Pending Events
        </Button>
      )}

      {isSuperadmin && (
        <Text size="md" className="mb-2" c="dimmed">
          Recurring Event ID: {recurringEvent.id}
        </Text>
      )}

      {/* Recurring Event Header Card */}
      <Card shadow="sm" padding="lg" radius="md" mb="xl" style={{ backgroundColor: '#fff7ed1a' }}>
        <Grid>
          <Grid.Col span={{ base: 12, md: 3 }}>
            {recurringEvent.image_url ? (
              <Image src={recurringEvent.image_url} height={200} fit="cover" radius="md" alt={recurringEvent.title} />
            ) : (
              <div
                style={{
                  height: 200,
                  backgroundColor: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 'var(--mantine-radius-md)',
                }}
              >
                <Text c="dimmed">No Image Available</Text>
              </div>
            )}
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 9 }}>
            <Stack>
              <Title order={2} className="text-white poppins">
                {recurringEvent.title}
              </Title>

              <Group>
                <Badge
                  color="orange"
                  variant="light"
                  radius="md"
                  size="lg"
                  leftSection={<IconCalendarRepeat size={14} />}
                  style={{ textTransform: 'none' }}
                >
                  {recurringEvent.recurrence_pattern} Recurring Event
                </Badge>

                <Badge color="orange" variant="light" size="lg" radius="md" style={{ textTransform: 'none' }}>
                  {recurringEvent.num_occurrences} Occurrences
                </Badge>
              </Group>

              {recurringEvent.recurrence_pattern === 'Monthly' &&
                recurringEvent.ordinal_occurrence &&
                recurringEvent.occurrence_day && (
                  <Text size="sm" c="dimmed">
                    This event occurs on the {recurringEvent.ordinal_occurrence.replace('_occurrence', '')}{' '}
                    {recurringEvent.occurrence_day.charAt(0).toUpperCase() + recurringEvent.occurrence_day.slice(1)} of
                    each month.
                  </Text>
                )}

              <Group mt="xs">
                <Menu shadow="md" width={200}>
                  <Menu.Target>
                    <Button variant="light" leftSection={<IconShare size={14} />}>
                      Share
                    </Button>
                  </Menu.Target>

                  <Menu.Dropdown>
                    {recurringEvent.slug && (
                      <Menu.Item leftSection={<IconCopy size={14} />} onClick={copyToClipboard}>
                        Copy Link
                      </Menu.Item>
                    )}

                    {events.length > 0 && (
                      <Menu.Item
                        leftSection={<IconCalendarPlus size={14} />}
                        component="a"
                        href={`/events/${events[0].slug}/ical.ics`}
                      >
                        Add to Calendar
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Stack>
          </Grid.Col>
        </Grid>
      </Card>

      {/* Events List */}
      <Title order={3} mb="md" className="text-white poppins">
        Events
      </Title>

      <Grid>
        {events.map((event) => (
          <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={event.id}>
            <EventCard event={event} onCardClick={() => handleEventClick(event)} />
          </Grid.Col>
        ))}

        {events.length === 0 && (
          <Grid.Col span={12}>
            <Alert icon={<IconInfoCircle size={16} />} title="No Events" color="gray" style={{ color: '#fff' }}>
              There are no events currently scheduled for this recurring event.
            </Alert>
          </Grid.Col>
        )}
      </Grid>
    </Container>
  );
};

export default RecurringEventShow;
